import React from 'react';

/**
 * UiTabs — the ONE canonical page-level tab strip for the whole POS.
 *
 * House rule: NO icons on tabs; the ACTIVE tab is filled with the brand color
 * (bg-primary-600 text-white); inactive tabs are neutral gray. Fill via props so
 * every tab strip across every page looks identical.
 *
 * Props:
 *   tabs     — [{ id, label }]
 *   active   — currently-selected id
 *   onChange — (id) => void
 *   size     — 'md' (default page tabs) | 'sm' (compact sub-tabs)
 *   className — extra classes on the strip wrapper (layout only)
 */
export function UiTabs({ tabs = [], active, onChange, size = 'md', className = '' }) {
  const pad = size === 'sm' ? 'px-3 py-1.5 text-sm' : 'px-3 sm:px-4 py-2 text-sm sm:text-base';
  return (
    <div className={`flex gap-2 flex-wrap ${className}`}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          className={`${pad} rounded-lg font-medium transition flex-shrink-0 ${
            active === tab.id
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/**
 * UiViewToggle — the ONE canonical segmented view switch (e.g. Table / Calendar / Graph).
 *
 * House rule: NO icons; ACTIVE segment filled with the brand color; segments joined
 * in a single bordered pill.
 *
 * Props:
 *   options  — [{ value, label }]
 *   value    — current value
 *   onChange — (value) => void
 *   className — extra classes on the wrapper (layout only)
 */
export function UiViewToggle({ options = [], value, onChange, className = '' }) {
  return (
    <div className={`inline-flex items-center overflow-hidden rounded-lg border border-gray-200 ${className}`}>
      {options.map((opt, i) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`px-3 py-2 text-sm transition ${i > 0 ? 'border-l border-gray-200' : ''} ${
            value === opt.value
              ? 'bg-primary-600 text-white'
              : 'bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default UiTabs;
